import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CalculateIcon from '@mui/icons-material/Calculate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleIcon from '@mui/icons-material/People';
import InsightsIcon from '@mui/icons-material/Insights';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddHomeIcon from '@mui/icons-material/AddHome';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import { DEFAULT_STRESS_TEST_RATE, DEFAULT_HDB_STRESS_TEST_RATE, useGlobalCalculatorSettings } from '../context/GlobalCalculatorSettingsContext';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import { SidebarNavigation, NavigationGroup } from '../../super-picker/components/sidebar';
import AppHeader from '../../super-picker/components/header/AppHeader';
import ProfileManager from '../pages/ProfileManager';
import { ProfileManagerLauncherContext } from '../context/ProfileManagerLauncherContext';
import ManageUsersDialog from './ManageUsersDialog';
import AnalyticsDashboardDialog from './AnalyticsDashboardDialog';
import EventRegistrationDialog from '../../common/dialogs/EventRegistrationDialog';
import { logCalculatorMenuClick, type SuperCalculatorKey } from '../services/analytics';

export interface LoginUserInfo {
  Name?: string;
  Photo?: string;
  CEANumber?: string;
}

interface SuperCalculatorLayoutProps {
  children: ReactNode;
  basePath: string;
  activeKey: 'purchase-planner' | 'affordability' | 'profile-manager' | 'buying-calculator' | 'selling-calculator' | 'decoupling-calculator';
  loginUser?: LoginUserInfo | null;
  isAdmin?: boolean;
  canAccessSellingCalculator?: boolean;
  canAccessDecouplingCalculator?: boolean;
}

const getPath = (basePath: string, segment: string): string =>
  basePath ? `${basePath}/${segment}` : `/${segment}`;

/** When set, opens from the Calculators menu (absolute https URL or app path e.g. `/classic-calculator`). */
const CLASSIC_CALCULATOR_URL: string | null = 'https://htns.sg/calc';

export default function SuperCalculatorLayout({
  children,
  basePath,
  activeKey,
  loginUser,
  isAdmin = false,
  canAccessSellingCalculator = false,
  canAccessDecouplingCalculator = false,
}: SuperCalculatorLayoutProps) {
  const navigate = useNavigate();
  const { currentTheme, setTheme } = useAppTheme();
  const { settings: globalSettings, updateSettings: updateGlobalSettings } = useGlobalCalculatorSettings();
  const { profiles, groups, selectedProfileId, getSelectedName, selectGroup, selectProfile } = useProfile();
  const theme = useTheme();
  const isMobileProfilePicker = useMediaQuery(theme.breakpoints.down('sm'));

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [calculatorsMenuAnchor, setCalculatorsMenuAnchor] = useState<null | HTMLElement>(null);
  const [profileSearchTerm, setProfileSearchTerm] = useState('');
  const [profileManagerDialogOpen, setProfileManagerDialogOpen] = useState(false);
  const [profileManagerInitialEditId, setProfileManagerInitialEditId] = useState<number | null>(null);
  const [profileManagerInitialEditGroupId, setProfileManagerInitialEditGroupId] = useState<number | null>(null);
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const [analyticsDashboardOpen, setAnalyticsDashboardOpen] = useState(false);
  const [eventRegistrationOpen, setEventRegistrationOpen] = useState(false);
  // EDM deep-link: ?redirect=training&eventId=XXX opens the training dialog
  // and auto-opens the inner Confirm Registration / Training Details dialog
  // for the matching event.
  const [autoOpenTrainingEventId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect') !== 'training') return null;
    return (params.get('eventId') || '').trim() || null;
  });
  useEffect(() => {
    if (autoOpenTrainingEventId) {
      setEventRegistrationOpen(true);
    }
  }, [autoOpenTrainingEventId]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState<string>(String(globalSettings.stressTestRate));
  const [hdbSettingsDraft, setHdbSettingsDraft] = useState<string>(String(globalSettings.hdbStressTestRate));
  const ceaNumber = loginUser?.CEANumber;

  const handleProfileManagerInitialEditConsumed = useCallback(() => {
    setProfileManagerInitialEditId(null);
  }, []);

  const handleProfileManagerInitialEditGroupConsumed = useCallback(() => {
    setProfileManagerInitialEditGroupId(null);
  }, []);

  const closeProfileMenu = useCallback(() => {
    setProfileMenuAnchor(null);
    setProfileSearchTerm('');
  }, []);

  const openProfileManager = useCallback((editProfileId: number | null = null) => {
    closeProfileMenu();
    setProfileManagerInitialEditGroupId(null);
    setProfileManagerInitialEditId(editProfileId);
    setProfileManagerDialogOpen(true);
  }, [closeProfileMenu]);

  const openProfileManagerForGroup = useCallback((groupId: number) => {
    closeProfileMenu();
    setProfileManagerInitialEditId(null);
    setProfileManagerInitialEditGroupId(groupId);
    setProfileManagerDialogOpen(true);
  }, [closeProfileMenu]);

  const profileManagerLauncher = useMemo(
    () => ({
      openEditProfile: (profileId: number) => {
        openProfileManager(profileId);
      },
      openEditGroup: (groupId: number) => {
        openProfileManagerForGroup(groupId);
      },
    }),
    [openProfileManager, openProfileManagerForGroup]
  );

  const navigateToCalculator = useCallback((segment: string, calculator: SuperCalculatorKey) => {
    logCalculatorMenuClick(calculator, ceaNumber);
    navigate(getPath(basePath, segment));
  }, [basePath, ceaNumber, navigate]);

  const filteredGroups = groups.filter((group) =>
    group.Name.toLowerCase().includes(profileSearchTerm.toLowerCase())
  );
  const filteredProfiles = profiles.filter((profile) =>
    profile.Name.toLowerCase().includes(profileSearchTerm.toLowerCase())
  );

  const handleProfilePick = (type: 'group' | 'profile', id: number) => {
    if (type === 'group') {
      selectGroup(id);
    } else {
      selectProfile(id);
    }
    closeProfileMenu();
  };

  const openSettingsDialog = useCallback(() => {
    setSettingsDraft(String(globalSettings.stressTestRate));
    setHdbSettingsDraft(String(globalSettings.hdbStressTestRate));
    setSettingsDialogOpen(true);
  }, [globalSettings.stressTestRate, globalSettings.hdbStressTestRate]);

  const closeSettingsDialog = useCallback(() => {
    setSettingsDialogOpen(false);
    setSettingsDraft(String(globalSettings.stressTestRate));
    setHdbSettingsDraft(String(globalSettings.hdbStressTestRate));
  }, [globalSettings.stressTestRate, globalSettings.hdbStressTestRate]);

  const saveSettingsDialog = useCallback(() => {
    const parsed = Number(settingsDraft);
    const parsedHdb = Number(hdbSettingsDraft);
    updateGlobalSettings({
      stressTestRate:
        Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_STRESS_TEST_RATE,
      hdbStressTestRate:
        Number.isFinite(parsedHdb) && parsedHdb > 0 ? parsedHdb : DEFAULT_HDB_STRESS_TEST_RATE,
    });
    setSettingsDialogOpen(false);
  }, [settingsDraft, hdbSettingsDraft, updateGlobalSettings]);

  const navigationGroups: NavigationGroup[] = useMemo(() => [
    {
      id: 'profile',
      label: 'Profile',
      items: [
        {
          id: 'manage-profile',
          label: 'Manage Profile',
          icon: <ManageAccountsIcon fontSize="small" />,
          active: activeKey === 'profile-manager',
          onClick: () => navigate(getPath(basePath, 'profile-manager')),
        },
      ],
    },
    ...(isAdmin ? [{
      id: 'admin',
      label: 'Admin',
      items: [
        {
          id: 'manage-users',
          label: 'Manage Users',
          icon: <PeopleIcon fontSize="small" />,
          active: false,
          onClick: () => setManageUsersOpen(true),
        },
        {
          id: 'analytics-dashboard',
          label: 'Analytics Dashboard',
          icon: <InsightsIcon fontSize="small" />,
          active: false,
          onClick: () => setAnalyticsDashboardOpen(true),
        },
      ],
    }] : []),
    {
      id: 'super-calculator',
      label: 'Super Calculator',
      items: [
        {
          id: 'affordability',
          label: 'Max Affordability Calculator',
          icon: <CalculateOutlinedIcon fontSize="small" />,
          active: activeKey === 'affordability',
          onClick: () => navigateToCalculator('affordability', 'affordability'),
        },
        {
          id: 'buying-calculator',
          label: 'Buying Calculator',
          icon: <AddHomeIcon fontSize="small" />,
          active: activeKey === 'buying-calculator',
          onClick: () => navigateToCalculator('buying-calculator', 'buying_calculator'),
        },
        ...(canAccessSellingCalculator ? [{
          id: 'selling-calculator',
          label: 'Selling Calculator',
          icon: <StorefrontIcon fontSize="small" />,
          active: activeKey === 'selling-calculator',
          onClick: () => navigateToCalculator('selling-calculator', 'selling_calculator'),
        }] : []),
        ...(canAccessDecouplingCalculator ? [{
          id: 'decoupling-calculator',
          label: 'Decoupling Calculator',
          icon: <CallSplitIcon fontSize="small" />,
          active: activeKey === 'decoupling-calculator',
          onClick: () => navigateToCalculator('decoupling-calculator', 'decoupling_calculator'),
        }] : []),
        {
          id: 'purchase-planner',
          label: 'Purchase Planner',
          icon: <HomeWorkIcon fontSize="small" />,
          active: activeKey === 'purchase-planner',
          onClick: () => navigateToCalculator('purchase-planner', 'purchase_planner'),
        },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      items: [
        {
          id: 'calculator-settings',
          label: 'Calculator Settings',
          icon: <SettingsIcon fontSize="small" />,
          active: false,
          onClick: openSettingsDialog,
        },
      ],
    },
    {
      id: 'classic-calculator',
      label: 'Classic Calculator',
      items: [
        {
          id: 'classic-calculator',
          label: 'Classic Calculator',
          icon: <CalculateIcon fontSize="small" />,
          active: false,
          disabled: !CLASSIC_CALCULATOR_URL,
          onClick: () => {
            if (!CLASSIC_CALCULATOR_URL) return;
            if (/^https?:\/\//i.test(CLASSIC_CALCULATOR_URL)) {
              window.location.assign(CLASSIC_CALCULATOR_URL);
            } else {
              navigate(CLASSIC_CALCULATOR_URL);
            }
          },
        },
      ],
    },
    {
      id: 'others',
      label: 'Others',
      items: [
        {
          id: 'register-for-training',
          label: 'Training',
          icon: <SchoolIcon fontSize="small" />,
          active: false,
          onClick: () => setEventRegistrationOpen(true),
        },
      ],
    },
  ], [
    activeKey,
    basePath,
    canAccessSellingCalculator,
    canAccessDecouplingCalculator,
    isAdmin,
    navigate,
    navigateToCalculator,
    openSettingsDialog,
  ]);

  const profilePickerContent = (
    <>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search profiles/groups..."
          value={profileSearchTerm}
          onChange={(event) => setProfileSearchTerm(event.target.value)}
          autoFocus={!isMobileProfilePicker}
        />
        {!profileSearchTerm && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
            {groups.length} groups, {profiles.length} profiles
          </Typography>
        )}
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {selectedProfileId && (
          <MenuItem
            onClick={() => { selectProfile(null); closeProfileMenu(); }}
            sx={{ color: 'var(--color-text-disabled)', fontSize: '0.85rem', py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ClearIcon sx={{ fontSize: 18, color: 'var(--color-text-faint)' }} />
            </ListItemIcon>
            Clear Selection
          </MenuItem>
        )}

        {filteredGroups.length > 0 && (
          <>
            <ListSubheader
              sx={{
                bgcolor: 'transparent',
                color: '#D97706',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: '32px',
                mt: 0.5,
              }}
            >
              <GroupIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: 'text-bottom' }} />
              Groups ({filteredGroups.length})
            </ListSubheader>
            {filteredGroups.map((group) => {
              const isActive = selectedProfileId === `group-${group.Id}`;
              return (
                <MenuItem
                  key={`group-${group.Id}`}
                  onClick={() => handleProfilePick('group', group.Id)}
                  selected={isActive}
                  sx={{
                    py: 0.75,
                    pl: 3,
                    pr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
                    '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <GroupIcon sx={{ fontSize: 18, color: '#D97706' }} />
                  </ListItemIcon>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--color-text-strong)' }}>
                      {group.Name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', fontSize: '0.7rem' }}>
                      {group.profiles?.length || 0} {group.profiles?.length === 1 ? 'profile' : 'profiles'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Edit group ${group.Name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfileManagerForGroup(group.Id);
                    }}
                    sx={{
                      color: 'var(--color-text-subtle)',
                      flexShrink: 0,
                      '&:hover': { color: '#D97706', bgcolor: 'rgba(217,119,6,0.12)' },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </MenuItem>
              );
            })}
          </>
        )}

        {filteredProfiles.length > 0 && (() => {
          const sorted = [...filteredProfiles].sort((a, b) => a.Name.localeCompare(b.Name));
          const grouped: Record<string, typeof sorted> = {};
          sorted.forEach((p) => {
            const letter = (p.Name[0] || '#').toUpperCase();
            if (!grouped[letter]) grouped[letter] = [];
            grouped[letter].push(p);
          });
          const letters = Object.keys(grouped).sort();

          return (
            <>
              <ListSubheader
                sx={{
                  bgcolor: 'transparent',
                  color: 'var(--color-text-subtle)',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  lineHeight: '32px',
                  mt: 0.5,
                }}
              >
                <PersonIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: 'text-bottom' }} />
                Individual Profiles ({filteredProfiles.length})
              </ListSubheader>
              {letters.map((letter) => (
                <React.Fragment key={letter}>
                  {!profileSearchTerm && filteredProfiles.length > 10 && (
                    <Box
                      sx={{
                        px: 2,
                        py: 0.25,
                        bgcolor: 'var(--color-surface-ghost)',
                        borderTop: '1px solid var(--color-border)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-text-faint)' }}
                      >
                        {letter}
                      </Typography>
                    </Box>
                  )}
                  {grouped[letter].map((profile) => {
                    const isActive = selectedProfileId === `profile-${profile.Id}`;
                    return (
                      <MenuItem
                        key={`profile-${profile.Id}`}
                        onClick={() => handleProfilePick('profile', profile.Id)}
                        selected={isActive}
                        sx={{
                          py: 0.5,
                          pl: 3,
                          pr: 1,
                          minHeight: 36,
                          color: 'var(--color-text-strong)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&.Mui-selected': { bgcolor: 'var(--color-surface-ghost-strong)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.12)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'var(--color-text-subtle)' }} />
                        </ListItemIcon>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 500, fontSize: '0.85rem', color: 'inherit', flex: 1, minWidth: 0 }}
                        >
                          {profile.Name}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Edit profile ${profile.Name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openProfileManager(profile.Id);
                          }}
                          sx={{
                            color: 'var(--color-text-subtle)',
                            flexShrink: 0,
                            '&:hover': { color: '#D97706', bgcolor: 'rgba(217,119,6,0.12)' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </MenuItem>
                    );
                  })}
                </React.Fragment>
              ))}
            </>
          );
        })()}

        {filteredGroups.length === 0 && filteredProfiles.length === 0 && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', fontSize: '0.85rem' }}>
              No results for &ldquo;{profileSearchTerm}&rdquo;
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'var(--color-border)', flexShrink: 0 }} />
      <Box sx={{ flexShrink: 0, px: 1, py: 0.75, bgcolor: 'var(--color-surface)' }}>
        <Button
          fullWidth
          size="small"
          variant="text"
          startIcon={<ManageAccountsIcon sx={{ color: '#D97706' }} />}
          onClick={() => openProfileManager(null)}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            color: '#D97706',
            fontWeight: 600,
            fontSize: '0.85rem',
          }}
        >
          Manage Profile
        </Button>
      </Box>
    </>
  );

  return (
    <ProfileManagerLauncherContext.Provider value={profileManagerLauncher}>
    <Box
      sx={{
        height: '100vh',
        bgcolor: 'var(--color-bg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <AppHeader
        currentTheme={currentTheme}
        userInfo={loginUser ?? null}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen((open) => !open)}
        onThemeToggle={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')}
        onTrainingClick={() => setEventRegistrationOpen(true)}
        showTrainingOnMobile
        compactOnMobile
        appLabel="SUPER Calculator"
        actions={(
          <>
            <Button
              size="small"
              startIcon={<CalculateIcon sx={{ display: { xs: 'none', md: 'inline-flex' } }} />}
              endIcon={<ExpandMoreIcon sx={{ display: { xs: 'none', md: 'inline-flex' } }} />}
              onClick={(event) => setCalculatorsMenuAnchor(event.currentTarget)}
              sx={{
                color: 'var(--color-text-strong)',
                border: '1px solid rgba(var(--color-text-rgb), 0.25)',
                textTransform: 'none',
                minWidth: { xs: 34, md: 'auto' },
                width: { xs: 34, md: 'auto' },
                px: { xs: 0, md: 1.5 },
                '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.08)' },
              }}
              aria-label="Open calculators menu"
            >
              <CalculateIcon sx={{ display: { xs: 'inline-flex', md: 'none' } }} />
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Calculators</Box>
            </Button>

          <IconButton
            onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                width: 34,
                height: 34,
                p: 0.5,
              color: 'var(--color-text-strong)',
              border: '1px solid rgba(var(--color-text-rgb), 0.25)',
              '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
            }}
            aria-label={selectedProfileId ? getSelectedName() || 'Profile' : 'Select Profile'}
          >
            <PersonIcon />
          </IconButton>
          <Button
            size="small"
            startIcon={<PersonIcon />}
            onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              color: 'var(--color-text-strong)',
              border: selectedProfileId ? '1px solid rgba(var(--color-text-rgb), 0.35)' : '1px solid rgba(var(--color-text-rgb), 0.18)',
              backgroundColor: selectedProfileId ? 'rgba(var(--color-text-rgb), 0.08)' : 'transparent',
              textTransform: 'none',
              maxWidth: { sm: 190, md: 300 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedProfileId ? getSelectedName() || 'Profile' : 'Select Profile'}
          </Button>
          </>
        )}
      />

      <Box sx={{ display: 'flex', flex: 1, mt: '64px', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        <SidebarNavigation
          groups={navigationGroups}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          showMobileToggle={false}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            bgcolor: 'var(--color-bg)',
            minWidth: 0,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            color: 'var(--color-text-strong)',
          }}
        >
          {children}
        </Box>
      </Box>

      <Dialog
        fullScreen
        open={Boolean(profileMenuAnchor) && isMobileProfilePicker}
        onClose={closeProfileMenu}
        disableRestoreFocus
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            height: '100dvh',
            maxHeight: '100dvh',
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1.25,
            pr: 1,
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
            Profile / group
          </Typography>
          <IconButton aria-label="Close profile picker" onClick={closeProfileMenu} size="small" sx={{ color: 'var(--color-text-strong)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
          }}
        >
          {profilePickerContent}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor) && !isMobileProfilePicker}
        onClose={closeProfileMenu}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 520,
            display: 'flex',
            flexDirection: 'column',
            // Popover paper defaults to overflowY: 'auto', which scrolls the whole menu and hides the footer.
            overflow: 'hidden',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
        MenuListProps={{
          disablePadding: true,
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            maxHeight: '100%',
            overflow: 'hidden',
          },
        }}
      >
        {profilePickerContent}
      </Menu>

      <Menu
        anchorEl={calculatorsMenuAnchor}
        open={Boolean(calculatorsMenuAnchor)}
        onClose={() => setCalculatorsMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 240,
            mt: 1.5,
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: '#D97706',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: '32px',
            mt: 0.5,
          }}
        >
          <CalculateIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: 'text-bottom' }} />
          Super Calculator
        </ListSubheader>
        <MenuItem
          onClick={() => {
            navigateToCalculator('affordability', 'affordability');
            setCalculatorsMenuAnchor(null);
          }}
          selected={activeKey === 'affordability'}
          sx={{
            pl: 3,
            '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
            '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CalculateOutlinedIcon fontSize="small" sx={{ color: '#D97706' }} />
          </ListItemIcon>
          Max Affordability Calculator
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigateToCalculator('buying-calculator', 'buying_calculator');
            setCalculatorsMenuAnchor(null);
          }}
          selected={activeKey === 'buying-calculator'}
          sx={{
            pl: 3,
            '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
            '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AddHomeIcon fontSize="small" sx={{ color: '#D97706' }} />
          </ListItemIcon>
          Buying Calculator
        </MenuItem>
        {canAccessSellingCalculator && (
          <MenuItem
            onClick={() => {
              navigateToCalculator('selling-calculator', 'selling_calculator');
              setCalculatorsMenuAnchor(null);
            }}
            selected={activeKey === 'selling-calculator'}
            sx={{
              pl: 3,
              '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
              '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <StorefrontIcon fontSize="small" sx={{ color: '#D97706' }} />
            </ListItemIcon>
            Selling Calculator
          </MenuItem>
        )}
        {canAccessDecouplingCalculator && (
          <MenuItem
            onClick={() => {
              navigateToCalculator('decoupling-calculator', 'decoupling_calculator');
              setCalculatorsMenuAnchor(null);
            }}
            selected={activeKey === 'decoupling-calculator'}
            sx={{
              pl: 3,
              '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
              '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CallSplitIcon fontSize="small" sx={{ color: '#D97706' }} />
            </ListItemIcon>
            Decoupling Calculator
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            navigateToCalculator('purchase-planner', 'purchase_planner');
            setCalculatorsMenuAnchor(null);
          }}
          selected={activeKey === 'purchase-planner'}
          sx={{
            pl: 3,
            '&.Mui-selected': { bgcolor: 'rgba(217,119,6,0.12)' },
            '&.Mui-selected:hover': { bgcolor: 'rgba(217,119,6,0.18)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HomeWorkIcon fontSize="small" sx={{ color: '#D97706' }} />
          </ListItemIcon>
          Purchase Planner
        </MenuItem>
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: 'var(--color-text-subtle)',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: '32px',
            mt: 0.5,
          }}
        >
          <CalculateOutlinedIcon sx={{ fontSize: 14, mr: 0.75, verticalAlign: 'text-bottom' }} />
          Classic Calculator
        </ListSubheader>
        <MenuItem
          disabled={!CLASSIC_CALCULATOR_URL}
          onClick={() => {
            if (!CLASSIC_CALCULATOR_URL) return;
            if (/^https?:\/\//i.test(CLASSIC_CALCULATOR_URL)) {
              window.location.assign(CLASSIC_CALCULATOR_URL);
            } else {
              navigate(CLASSIC_CALCULATOR_URL);
            }
            setCalculatorsMenuAnchor(null);
          }}
          sx={{ pl: 3 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CalculateIcon fontSize="small" sx={{ color: 'var(--color-text-subtle)' }} />
          </ListItemIcon>
          Classic Calculator
        </MenuItem>
      </Menu>

      {isAdmin && (
        <ManageUsersDialog open={manageUsersOpen} onClose={() => setManageUsersOpen(false)} />
      )}

      {isAdmin && (
        <AnalyticsDashboardDialog
          open={analyticsDashboardOpen}
          onClose={() => setAnalyticsDashboardOpen(false)}
        />
      )}

      <EventRegistrationDialog
        open={eventRegistrationOpen}
        onClose={() => setEventRegistrationOpen(false)}
        category="Super Calculator"
        title="Super Calculator Training and Registration"
        emptyMessage="No Super Calculator events available for registration."
        ceaNumber={ceaNumber}
        initialEventId={autoOpenTrainingEventId}
      />

      <Dialog
        open={settingsDialogOpen}
        onClose={closeSettingsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Settings</DialogTitle>
        <DialogContent>
          {/* Two floors, because they are set by different regulators: MAS prescribes the
              medium-term interest rate floor for bank loans, while HDB applies its own
              (lower) floor when computing the eligible concessionary loan amount. */}
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Stress Test Rate — Bank Loan (%)"
              type="number"
              value={settingsDraft}
              onChange={(event) => setSettingsDraft(event.target.value)}
              helperText="MAS medium-term interest rate floor for TDSR / MSR on bank loans. Shared across calculators."
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <TextField
              fullWidth
              label="Stress Test Rate — HDB Loan (%)"
              type="number"
              value={hdbSettingsDraft}
              onChange={(event) => setHdbSettingsDraft(event.target.value)}
              helperText="HDB's interest rate floor for computing the eligible HDB concessionary loan amount."
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pb: 3 }}>
          <Button onClick={closeSettingsDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveSettingsDialog}>Save</Button>
        </Box>
      </Dialog>

      <Dialog
        open={profileManagerDialogOpen}
        onClose={() => {
          setProfileManagerDialogOpen(false);
          setProfileManagerInitialEditId(null);
          setProfileManagerInitialEditGroupId(null);
        }}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            py: 1.5,
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Profile Manager
          </Typography>
          <IconButton
            aria-label="Close profile manager"
            onClick={() => {
              setProfileManagerDialogOpen(false);
              setProfileManagerInitialEditId(null);
              setProfileManagerInitialEditGroupId(null);
            }}
            sx={{ color: 'var(--color-text-strong)' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2 },
            bgcolor: 'var(--color-bg)',
            overflowY: 'auto',
          }}
        >
          <ProfileManager
            noCard
            initialEditProfileId={profileManagerInitialEditId}
            onInitialEditConsumed={handleProfileManagerInitialEditConsumed}
            initialEditGroupId={profileManagerInitialEditGroupId}
            onInitialEditGroupConsumed={handleProfileManagerInitialEditGroupConsumed}
            onNavigateToCalculator={() => {
              setProfileManagerDialogOpen(false);
              setProfileManagerInitialEditId(null);
              setProfileManagerInitialEditGroupId(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
    </ProfileManagerLauncherContext.Provider>
  );
}
